import "../../../components/common/Tables/DataTable.css"
import React, {useState} from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import MDEditor from '@uiw/react-md-editor';
import RootState from "../../../redux/states/root.state";
import ModalShell from "../../../components/common/Modals/ModalShell";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'title', label:'Title'},
  {id:'body', label:'Body'},
  {id:'updated_at', label:'Last Updated'},
  {id:'created_at', label:'Created'},
  {id:'edit', label:'Edit', align:'center'},
  {id:'delete', label:'Delete', align:'center'},
];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
};

const formatDate = (value: any) =>
  value ? <span className="dt-date">{new Date(value).toLocaleString()}</span> : <span className="dt-na">—</span>;

interface CommandsTableProps {
  setCommandToModify: any;
  setEditOpen: any;
  setDeleteOpen: any;
  // Page controls (filters toggle, Add Command) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const CommandsTable: React.FC<CommandsTableProps> = ({setCommandToModify, setEditOpen, setDeleteOpen, toolbar}) => {
  const {canAccess: canUpdate} = usePermissionCheck(['update_api_command']);
  const {canAccess: canDelete} = usePermissionCheck(['delete_api_command']);
  const {data:commands, loading, meta} = useSelector((state: RootState) => state.commands);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sorted by the API (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

  const [viewCommand, setViewCommand] = useState<any>(null);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  };

  // Edit / Delete only for those allowed
  const visibleHeaders = headers.filter((header) =>
    (header.id !== 'edit' || canUpdate) && (header.id !== 'delete' || canDelete)
  );
  const pinned = visibleHeaders.map((header) => header.id).filter((id) => id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const renderCell = (id: string, command: any) => {
    switch (id) {
      case 'title':
        return <span className="dt-strong">{command?.title ?? '—'}</span>;

      case 'body': {
        const body = command?.body ? JSON.stringify(command.body) : '';
        return body ? (
          <Tooltip title="View the full body" arrow>
            <button type="button" className="dt-title-btn dt-mono" onClick={() => setViewCommand(command)}>
              {body}
            </button>
          </Tooltip>
        ) : <span className="dt-na">—</span>;
      }

      case 'updated_at':
        return formatDate(command?.updated_at);

      case 'created_at':
        return formatDate(command?.created_at);

      case 'edit':
        return (
          <Tooltip title="Edit command" arrow>
            <button type="button" className="dt-act" onClick={() => { setCommandToModify(command); setEditOpen(true); }} aria-label="Edit command">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete command" arrow>
            <button type="button" className="dt-act danger" onClick={() => { setCommandToModify(command); setDeleteOpen(true); }} aria-label="Delete command">
              <TrashIcon/>
            </button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {viewCommand &&
        <ModalShell
          open={!!viewCommand}
          onClose={() => setViewCommand(null)}
          size="lg"
          title={viewCommand?.title}
          actions={<button className="btn btn-primary" onClick={() => setViewCommand(null)}>Close</button>}
        >
          <div className="markdown-body" data-color-mode={localStorage.getItem('data-theme') ?? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')}>
            <MDEditor.Markdown source={JSON.stringify(viewCommand?.body, null, 2)} className="markdown-preview" />
          </div>
        </ModalShell>}

      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading commands"/></div>
        ) : commands?.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {visibleHeaders.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id !== 'edit' && header.id !== 'delete' ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={() => handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {commands.map((command: any, i: number) => (
                <tr key={command?.id ?? i}>
                  {visibleHeaders.map((header) => (
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, command)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Commands Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={commands?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Command' : 'Commands'}
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default CommandsTable;
